import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const BACKEND_URL = '';

/**
 * GlobalSideBanner — renders the active side banner as a fixed,
 * right-margin widget on every page for large screens (lg and up).
 *
 * Mounted once near the app root so we don't have to add `<SponsorBanner
 * type="side"/>` to every individual page. The placement we ask for is
 * derived from the route, with a small set of overrides where the page
 * already has its own dedicated side slot (e.g. the Forum sidebar).
 *
 * Mobile: hidden. Side banners would take too much real estate on a
 * phone — top banners still appear inline on those pages.
 */
const ROUTE_PLACEMENT = [
  // Routes where we should NOT show any side banner — landing page,
  // auth flows, modals where the floating banner would clash visually.
  { match: /^\/$/,           placement: null },
  { match: /^\/login/,       placement: null },
  { match: /^\/signup/,      placement: null },
  { match: /^\/register/,    placement: null },
  { match: /^\/forgot/,      placement: null },
  { match: /^\/reset/,       placement: null },
  { match: /^\/verify/,      placement: null },
  // All main pages now own their own inline side rail (PageSideRail
  // component, mirroring the Forum layout) so we suppress the floating
  // GlobalSideBanner on these routes to avoid double-rendering ads.
  { match: /^\/forum/,            placement: null },
  { match: /^\/(progress|dashboard)/,        placement: null },
  { match: /^\/britelabs/,        placement: null },
  { match: /^\/events/,           placement: null },
  { match: /^\/courses/,          placement: null },
  { match: /^\/news/,             placement: null },
  { match: /^\/companies/,        placement: null },
  { match: /^\/partners/,         placement: null },
  { match: /^\/members/,          placement: null },
  { match: /^\/messages/,         placement: null },
  { match: /^\/jobs/,             placement: null },
  { match: /^\/profile/,          placement: null },
  { match: /^\/research/,         placement: null },
  { match: /^\/learning/,         placement: null },
  { match: /^\/keynotes/,         placement: null },
  { match: /^\/britecast/,        placement: null },
  { match: /^\/admin/,            placement: null },
  { match: /^\/feedback/,         placement: null },
  { match: /^\/sponsors/,         placement: null },
  // Site-wide fallback for anything else.
];

function placementForPath(pathname) {
  for (const r of ROUTE_PLACEMENT) {
    const m = pathname.match(r.match);
    if (m) {
      if (r.placement === null) return null; // explicit skip
      return typeof r.placement === 'function' ? r.placement(m) : r.placement;
    }
  }
  return 'app'; // site-wide fallback
}

export default function GlobalSideBanner() {
  const { pathname } = useLocation();
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    const placement = placementForPath(pathname);
    if (!placement) { setBanner(null); return; }
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/banners?placement=${encodeURIComponent(placement)}`, {
      credentials: 'include',
    })
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        if (cancelled) return;
        // Pick the first "side" type for the page; backend has already
        // applied page-specific override over site-wide.
        const sideBanner = Array.isArray(list) ? list.find(b => b.banner_type === 'side') : null;
        setBanner(sideBanner || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [pathname]);

  // Toggle a body class while a side banner is rendered so the rest of
  // the app can reserve right-side space and not get overlapped. The
  // companion CSS rule lives in `index.css` and only kicks in on xl+
  // screens (where the banner is actually visible).
  useEffect(() => {
    if (banner) {
      document.body.classList.add('has-side-banner');
    } else {
      document.body.classList.remove('has-side-banner');
    }
    return () => document.body.classList.remove('has-side-banner');
  }, [banner]);

  if (!banner) return null;

  const trackClick = () => {
    try {
      fetch(`${BACKEND_URL}/api/banners/${banner.banner_id}/click`, {
        method: 'POST', credentials: 'include', keepalive: true,
      }).catch(() => {});
    } catch {}
  };

  const img = (
    <img
      src={banner.image_url}
      alt="Sponsor"
      className="w-full max-h-[300px] object-contain rounded-md shadow-sm bg-white"
      loading="lazy"
    />
  );

  return (
    <aside
      data-testid="global-side-banner"
      // Hidden on mobile / tablet. Fixed to the right edge of the
      // viewport, vertically centred, with a slim max-width so it never
      // crowds content on smaller laptops.
      className="hidden xl:block fixed right-4 top-1/2 -translate-y-1/2 z-30 w-[220px]"
    >
      <p className="text-[10px] text-slate-400 mb-1 text-center">Sponsored</p>
      {banner.click_url ? (
        <a
          href={banner.click_url}
          target="_blank"
          rel="noopener noreferrer sponsored"
          onClick={trackClick}
          onAuxClick={trackClick}
          className="block transition-opacity hover:opacity-90"
          data-testid="global-side-banner-link"
        >
          {img}
        </a>
      ) : img}
    </aside>
  );
}
